import { Controller } from '@hotwired/stimulus';
import { get, supported } from '@github/webauthn-json';
import { postJson } from '../../lib/passkeys';

export default class extends Controller {
  static targets = [
    'passkeyStep',
    'passwordStep',
    'waiting',
    'waitingText',
    'submitButton',
    'submitLabel',
    'spinnerIcon',
    'error',
    'errorMessage',
  ];

  static values = {
    challengeUrl: String,
    sessionUrl: String,
    step: String,
    cancelledMessage: String,
    genericMessage: String,
  };

  connect() {
    this.waitingMessage = this.hasWaitingTextTarget
      ? this.waitingTextTarget.textContent
      : null;
    this.idleLabel = this.hasSubmitLabelTarget
      ? this.submitLabelTarget.textContent
      : null;

    if (this.stepValue !== 'passkey') return;

    if (!supported()) this.#openPasswordStep();
  }

  disconnect() {
    this.abortController?.abort();
  }

  async authenticate() {
    this.abortController?.abort();
    this.#hideError();
    this.#enterWaitingState();
    this.abortController = new AbortController();

    try {
      const options = await this.#requestChallenge();

      if (options) {
        await this.#openSession(
          await get({ publicKey: options, signal: this.abortController.signal })
        );
      }
    } catch (error) {
      this.#showError(this.#messageFor(error));
    } finally {
      this.#leaveWaitingState();
    }
  }

  showPasswordStep(event) {
    event.preventDefault();
    this.abortController?.abort();
    this.#openPasswordStep();
  }

  showPasskeyStep(event) {
    event.preventDefault();
    this.#hideError();
    this.passwordStepTarget.classList.add('d-none');
    this.passkeyStepTarget.classList.remove('d-none');
  }

  #openPasswordStep() {
    this.#hideError();

    if (this.hasPasskeyStepTarget) this.passkeyStepTarget.classList.add('d-none');

    this.passwordStepTarget.classList.remove('d-none');
  }

  async #requestChallenge() {
    const response = await postJson(this.challengeUrlValue, {});

    if (!response.ok) {
      this.#showError(this.genericMessageValue);
      return null;
    }

    return response.json();
  }

  async #openSession(credential) {
    const response = await postJson(this.sessionUrlValue, {
      users_passkey: { credential: credential },
    });
    const body = await response.json();

    if (!response.ok) {
      this.#showError(body.error || this.genericMessageValue);
      return;
    }

    window.location.assign(body.redirect_url);
  }

  #enterWaitingState() {
    this.submitButtonTarget.disabled = true;
    this.waitingTarget.classList.remove('d-none');
    this.waitingTarget.classList.add('d-flex');
    this.spinnerIconTarget.classList.remove('d-none');

    // Same reason: re-assign the text once the status region is on screen.
    if (this.hasWaitingTextTarget) {
      this.waitingTextTarget.textContent = this.waitingMessage;
    }
  }

  #leaveWaitingState() {
    this.submitButtonTarget.disabled = false;
    this.submitLabelTarget.textContent = this.idleLabel;
    this.waitingTarget.classList.add('d-none');
    this.waitingTarget.classList.remove('d-flex');
    this.spinnerIconTarget.classList.add('d-none');
  }

  #messageFor(error) {
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
      return this.cancelledMessageValue;
    }

    return this.genericMessageValue;
  }

  #showError(message) {
    // Reveal before writing: a mutation inside display:none is never announced.
    this.errorTarget.classList.remove('d-none');
    this.errorMessageTarget.textContent = message;
  }

  #hideError() {
    this.errorMessageTarget.textContent = '';
    this.errorTarget.classList.add('d-none');
  }
}
